import React, { useState, useRef, useEffect } from 'react';
import './QueryChat.css';
import API_CONFIG from '../config/api';
import { useTheme as useCustomTheme } from '../theme/ThemeProvider';
import { SV } from '../automation/stratovaiTestIds';

function plainChatLine(line) {
  if (typeof line !== 'string') {
    return line;
  }
  return line
    .replace(/^#{1,6}\s+/, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/(^|[^*])\*(?!\*)(.+?)(?<!\*)\*(?![^*])/g, '$1$2');
}

// Security configuration (same as App.js)
const TOKEN_KEY = 'stratovai_access_token';
const REFRESH_TOKEN_KEY = 'stratovai_refresh_token';

// Secure API client with token management (same as App.js)
class SecureAPIClient {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL || '';
  }

  getAccessToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setTokens(accessToken, refreshToken) {
    localStorage.setItem(TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  clearTokens() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  isTokenExpired(token) {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch {
      return true;
    }
  }

  async refreshAccessToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      this.setTokens(data.access_token, refreshToken);
      return data.access_token;
    } catch (error) {
      this.clearTokens();
      throw error;
    }
  }

  async makeRequest(url, options = {}) {
    let accessToken = this.getAccessToken();

    if (this.isTokenExpired(accessToken)) {
      try {
        accessToken = await this.refreshAccessToken();
      } catch (error) {
        window.location.href = '/';
        throw error;
      }
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      ...options.headers,
    };

    try {
      const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`;
      const response = await fetch(fullUrl, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        try {
          accessToken = await this.refreshAccessToken();
          headers.Authorization = `Bearer ${accessToken}`;
          
          const retryResponse = await fetch(fullUrl, {
            ...options,
            headers,
          });

          if (!retryResponse.ok) {
            throw new Error(`HTTP ${retryResponse.status}: ${retryResponse.statusText}`);
          }

          return await retryResponse.json();
        } catch (refreshError) {
          this.clearTokens();
          window.location.href = '/';
          throw refreshError;
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Get embedding status for a project
  async getEmbeddingStatus(projectId) {
    try {
      const response = await this.makeRequest(`/embeddings/${projectId}/status`);
      return response;
    } catch (error) {
      console.error('Failed to get embedding status:', error);
      throw error;
    }
  }
}

// Create API client instance
const apiClient = new SecureAPIClient();

const QueryChat = ({ selectedProject, authenticatedUser }) => {
  const { isDarkMode } = useCustomTheme();
  
  // Generate a unique chat session key based on user and project
  const getChatSessionKey = () => {
    if (!authenticatedUser || !selectedProject) return null;
    return `chat_session_${authenticatedUser.user_id}_${selectedProject.project_id}`;
  };

  // Load chat messages from localStorage or use default
  const loadChatSession = () => {
    const sessionKey = getChatSessionKey();
    if (!sessionKey) {
      return [{
        id: 1,
        type: 'bot',
        content: 'Hello! I\'m your Product Intelligence Assistant. Ask me anything about your platform - features, test coverage, capabilities, or any product-related questions!',
        timestamp: new Date()
      }];
    }

    try {
      const stored = localStorage.getItem(sessionKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Convert timestamp strings back to Date objects
        return parsed.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      console.error('Error loading chat session:', error);
    }

    // Return default welcome message if no session found
    return [{
      id: 1,
      type: 'bot',
      content: 'Hello! I\'m your Product Intelligence Assistant. Ask me anything about your platform - features, test coverage, capabilities, or any product-related questions!',
      timestamp: new Date()
    }];
  };

  // Save chat messages to localStorage
  const saveChatSession = (messages) => {
    const sessionKey = getChatSessionKey();
    if (sessionKey) {
      try {
        localStorage.setItem(sessionKey, JSON.stringify(messages));
      } catch (error) {
        console.error('Error saving chat session:', error);
      }
    }
  };

  const [messages, setMessages] = useState(loadChatSession);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const isInitialLoad = useRef(true);
  
  // Phase 3: Embedding status and filters (removed - no longer used)
  // const [embeddingStatus, setEmbeddingStatus] = useState(null);
  // const [filters, setFilters] = useState({
  //   platform: '',
  //   module: '',
  //   test_type: '',
  //   priority: '',
  //   status: 'approved'
  // });
  // const [showFilters, setShowFilters] = useState(false);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    saveChatSession(messages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Load chat session when user or project changes
  useEffect(() => {
    const newMessages = loadChatSession();
    setMessages(newMessages);
    isInitialLoad.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedUser?.user_id, selectedProject?.project_id]);

  // Phase 3: Fetch embedding status (removed - no longer displayed)
  // useEffect(() => {
  //   const fetchEmbeddingStatus = async () => {
  //     if (!selectedProject?.project_id) return;
  //     
  //     try {
  //       const response = await apiClient.getEmbeddingStatus(selectedProject.project_id);
  //       if (response.success) {
  //         setEmbeddingStatus(response.status);
  //       }
  //     } catch (error) {
  //       console.error('Failed to fetch embedding status:', error);
  //       // Silently fail - not critical
  //     }
  //   };
  //   
  //   fetchEmbeddingStatus();
  //   // Refresh every 30 seconds to show indexing progress
  //   const interval = setInterval(fetchEmbeddingStatus, 30000);
  //   return () => clearInterval(interval);
  // }, [selectedProject?.project_id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!isInitialLoad.current) {
      scrollToBottom();
    } else {
      isInitialLoad.current = false;
    }
  }, [messages]);

  // Reset chat session function
  const resetChat = () => {
    const defaultMessage = [{
      id: Date.now(),
      type: 'bot',
      content: 'Hello! I\'m your Product Intelligence Assistant. Ask me anything about your platform - features, test coverage, capabilities, or any product-related questions!',
      timestamp: new Date()
    }];
    setMessages(defaultMessage);
    saveChatSession(defaultMessage);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    try {
      // Send query without filters (filter UI removed)
      const data = await apiClient.makeRequest('/test/query', {
        method: 'POST',
        body: JSON.stringify({ 
          question: inputValue,
          project_id: selectedProject?.project_id ? parseInt(selectedProject.project_id) : null,
          filters: null
        })
      });
      
      if (!data.answer) {
        throw new Error('No answer received from server.');
      }
      
      // Extract source metadata if available; omit for refusals/out-of-scope
      const refusalSnippet = 'outside the available product context';
      const isRefusal =
        !data.source_metadata?.length ||
        data.source === 'out_of_scope' ||
        data.source === 'insufficient_context' ||
        (typeof data.answer === 'string' &&
          data.answer.toLowerCase().includes(refusalSnippet));
      const sourceMetadata = isRefusal ? [] : (data.source_metadata || []);
      
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: data.answer,
        timestamp: new Date(),
        sourceMetadata: sourceMetadata,
        responseSource: data.source || 'knowledge_base',
      };
      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      setError('Something went wrong. Please try again later.');
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`query-chat ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-testid={SV.chat.root}>
      <div className="chat-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <h3>💡 Product Intelligence</h3>
            <p>Know Your Product. Just Ask.</p>
          </div>
          <button 
            type="button"
            data-testid={SV.chat.reset}
            onClick={resetChat}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '0.5rem 0.75rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.8rem',
              transition: 'all 0.2s ease'
            }}
            title="Reset chat session"
          >
            🔄 Reset
          </button>
        </div>
      </div>

      <div className="chat-messages" data-testid={SV.chat.messages}>
        {messages.map((message) => (
          <div 
            key={message.id} 
            className={`message ${message.type} ${message.isError ? 'error' : ''}`}
          >
            <div className="message-content">
              {message.type === 'bot' && (
                <div className="bot-avatar">🤖</div>
              )}
              <div className="message-text">
                {typeof message.content === 'string' ? (
                  <>
                    {message.content.split('\n').map((line, index, lines) => {
                      const trimmedLine = line.trim();
                      const isSourceLine = /^sources?:/i.test(trimmedLine);

                      if (
                        isSourceLine &&
                        message.sourceMetadata &&
                        message.sourceMetadata.length > 0 &&
                        message.responseSource !== 'out_of_scope' &&
                        message.responseSource !== 'insufficient_context'
                      ) {
                        const jiraSources = message.sourceMetadata.filter(
                          (sourceMeta) =>
                            sourceMeta.source_key &&
                            (sourceMeta.source_type === 'jira_issue' ||
                              sourceMeta.source_type === 'jira' ||
                              !sourceMeta.source_type)
                        );
                        const sourcesToRender =
                          jiraSources.length > 0 ? jiraSources : message.sourceMetadata;
                        const label = sourcesToRender.length > 1 ? 'Sources' : 'Source';

                        return (
                          <div
                            key={index}
                            style={{
                              marginTop: '0.75rem',
                              paddingTop: '0.75rem',
                              borderTop: '1px solid rgba(0,0,0,0.1)',
                            }}
                          >
                            <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.35rem' }}>
                              {label}:
                            </div>
                            {sourcesToRender.map((sourceMeta, sourceIndex) => {
                              const jiraKey = sourceMeta.source_key;
                              const title = sourceMeta.source_title || jiraKey || 'Unknown';
                              const hasLink =
                                sourceMeta.source_url &&
                                (sourceMeta.source_type === 'jira_issue' ||
                                  sourceMeta.source_type === 'jira');

                              return (
                                <div key={`${index}-${sourceIndex}`} style={{ marginBottom: '0.25rem' }}>
                                  {hasLink ? (
                                    <a
                                      href={sourceMeta.source_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: '#3b82f6',
                                        textDecoration: 'none',
                                        fontWeight: 500,
                                        fontSize: '0.85rem',
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.textDecoration = 'underline';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.textDecoration = 'none';
                                      }}
                                    >
                                      {jiraKey ? `${jiraKey} – ${title}` : title}
                                    </a>
                                  ) : (
                                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                      {jiraKey ? `${jiraKey} – ${title}` : title}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      }

                      // Regular line
                      return (
                        <div key={index}>
                          {plainChatLine(line)}
                          {index < lines.length - 1 && <br />}
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <div>Invalid message format.</div>
                )}
              </div>
            </div>
            <div className="message-timestamp">
              {formatTimestamp(message.timestamp)}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="message bot">
            <div className="message-content">
              <div className="bot-avatar">🤖</div>
              <div className="message-text">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="chat-error-banner">
          {error}
        </div>
      )}

      <div className="chat-input">
        <div className="input-container">
          <textarea
            data-testid={SV.chat.input}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your product, features, test coverage, capabilities..."
            disabled={isLoading}
            rows={1}
          />
          <button 
            type="button"
            data-testid={SV.chat.send}
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="send-button"
            title={isLoading ? "Processing..." : (!inputValue.trim() ? "Type a message to send" : "Send message")}
          >
            {isLoading ? '⏳' : '📤'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QueryChat; 